// src/components/BuukkeriBookingEmbed.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * Yleinen Buukkeri-embed palveluntarjoajien omille sivuille.
 *
 * Käyttö:
 * <BuukkeriBookingEmbed slug="bilebandi-joyride" />
 *
 * Tai halutessa suora src:
 * <BuukkeriBookingEmbed src="https://buukkeri.fi/embed/bilebandi-joyride" />
 *
 * Tämä komponentti:
 * - näyttää otsikon ja kuvauksen
 * - upottaa Buukkerin embed-sivun iframeen
 * - kuuntelee embedin lähettämää oikeaa korkeutta postMessage:lla
 * - käyttää fallback-korkeutta varmuuden vuoksi ennen kuin oikea korkeus saadaan
 */
export default function BuukkeriBookingEmbed({
  slug,
  src,
  embedKey,
  embedTheme,
  title = "Tarkista vapaat päivät ja lähetä tiedustelu Buukkerin kautta",
  description = "Palveluntarjoajan buukkauspalvelun toteuttaa Buukkeri. Valitse päivä kalenterista ja lähetä tiedustelu helposti verkossa.",
  accentColor = "#05F2F2",
  titleColor = "#05F2F2",
  descriptionColor = "#F2F2F2",
  cardBackground = "rgba(7, 58, 89, 0.78)",
  cardBorderColor = "rgba(5, 242, 242, 0.28)",
  cardGlowColor = "rgba(5, 242, 242, 0.18)",
  poweredByColor = "rgba(242, 242, 242, 0.78)",
  sectionId = "varaa",
  minHeightMobile = 1250,
  minHeightTablet = 1150,
  minHeightSmallDesktop = 980,
  minHeightDesktop = 900,
  showPoweredBy = true,
  poweredByUrl = "https://buukkeri.fi",
  allowedOrigins = [
    "https://buukkeri.fi",
    "https://www.buukkeri.fi",
    "https://staging.buukkeri.fi",
  ],
}) {
  const iframeRef = useRef(null);
  const hasReceivedEmbedHeightRef = useRef(false);
  const [frameHeight, setFrameHeight] = useState(minHeightDesktop);

  /**
   * Palauttaa ensin järkevän oletuskorkeuden viewportin mukaan.
   * Tämä on vain varmistus siihen asti, että embed lähettää oikean korkeuden.
   */
  useEffect(() => {
    const updateFallbackHeight = () => {
      // Kun embed on kerran lähettänyt oikean korkeuden,
      // fallback ei saa enää yliajaa sitä resizeissä.
      if (hasReceivedEmbedHeightRef.current) return;
      const width = window.innerWidth;

      if (width < 480) {
        setFrameHeight(minHeightMobile);
        return;
      }

      if (width < 768) {
        setFrameHeight(minHeightTablet);
        return;
      }

      if (width < 1024) {
        setFrameHeight(minHeightSmallDesktop);
        return;
      }

      setFrameHeight(minHeightDesktop);
    };

    updateFallbackHeight();
    window.addEventListener("resize", updateFallbackHeight);

    return () => {
      window.removeEventListener("resize", updateFallbackHeight);
    };
  }, [
    minHeightMobile,
    minHeightTablet,
    minHeightSmallDesktop,
    minHeightDesktop,
  ]);

  /**
   * Kuunnellaan iframe-sisällön lähettämää korkeutta.
   * Hyväksytään viestit vain sallituista origineista.
   */
  useEffect(() => {
    const allowed = new Set(allowedOrigins);

    const onMessage = (event) => {
      if (!allowed.has(event.origin)) return;

      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow || event.source !== iframeWindow) return;

      const data = event.data;
      if (!data || !data.type) return;

      if (data.type === "bbx:embed-height") {
        const nextHeight = Number(data.height);
        if (!Number.isFinite(nextHeight)) return;

        // Suojataan arvo järkevään haarukkaan.
        const clamped = Math.max(700, Math.min(nextHeight, 5000));
        hasReceivedEmbedHeightRef.current = true;
        setFrameHeight(clamped);
        return;
      }

      if (data.type === "bbx:scroll-to-form") {
        if (
          typeof window === "undefined" ||
          !window.matchMedia ||
          !window.matchMedia("(max-width: 767.98px)").matches
        ) {
          return;
        }

        const iframeEl = iframeRef.current;
        if (!iframeEl) return;

        const rawOffsetTop = Number(data.offsetTop);
        const offsetTop = Number.isFinite(rawOffsetTop)
          ? Math.max(0, rawOffsetTop)
          : 0;

        requestAnimationFrame(() => {
          const header =
            document.querySelector("[data-sticky-header]") ||
            document.querySelector("header.sticky") ||
            document.querySelector("header.fixed") ||
            document.querySelector(".site-header.sticky");

          const headerH = header ? header.getBoundingClientRect().height : 0;
          const EXTRA_TOP_SPACE = 72;

          const y =
            window.scrollY +
           iframeEl.getBoundingClientRect().top +
            offsetTop -
            headerH -
            EXTRA_TOP_SPACE;

          window.scrollTo({
            top: Math.max(0, y),
            behavior: "smooth",
          });
        });
      }
    };

    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [allowedOrigins]);

  /**
   * Jos src annetaan suoraan, käytetään sitä.
   * Muuten rakennetaan slugista oletusosoite.
   */

  const resolvedSrc = useMemo(() => {
    if (src) return src;

    const base = `https://buukkeri.fi/embed/${encodeURIComponent(slug || "")}`;
    const params = new URLSearchParams();
       const parentHost =
      typeof window !== "undefined"
        ? String(window.location.hostname || "").trim().toLowerCase()
        : "";

    if (embedKey) {
      params.set("key", embedKey);
    }

    if (parentHost) {
      params.set("host", parentHost);
    }

    if (embedTheme) {
      params.set("theme", embedTheme);
    }

    const query = params.toString();
    return query ? `${base}?${query}` : base;
  }, [src, slug, embedKey, embedTheme]);

  return (
    <section
      id={sectionId}
      style={styles.section}
      aria-labelledby="buukkeri-booking-title"
    >
      <div style={styles.inner}>
        <div style={styles.header}>
          <h2
            id="buukkeri-booking-title"
            style={{
              ...styles.title,
              color: titleColor,
              textShadow: `0 0 18px ${cardGlowColor}`,
            }}
          >
            {title}
          </h2>

          <p
            style={{
              ...styles.description,
              color: descriptionColor,
            }}
          >
            {description}
          </p>
        </div>

        <div
          style={{
            ...styles.embedCard,
            background: cardBackground,
            border: `1px solid ${cardBorderColor}`,
            boxShadow:
              cardGlowColor === "transparent" && cardBorderColor === "transparent"
                ? "none"
                : `0 14px 40px rgba(0,0,0,0.35), 0 0 24px ${cardGlowColor}`,
          }}
        >
          <iframe
            ref={iframeRef}
            src={resolvedSrc}
            title="Buukkerin varauskalenteri ja tarjouslomake"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            style={{
              ...styles.iframe,
              height: `${frameHeight}px`,
            }}
          />
        </div>
        {showPoweredBy && (
          <p
            style={{
              ...styles.poweredBy,
              color: poweredByColor,
            }}
          >
          </p>
        )}
      </div>
    </section>
  );
}

BuukkeriBookingEmbed.propTypes = {
  slug: PropTypes.string,
  src: PropTypes.string,
  embedKey: PropTypes.string,
  embedTheme: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  accentColor: PropTypes.string,
  titleColor: PropTypes.string,
  descriptionColor: PropTypes.string,
  cardBackground: PropTypes.string,
  cardBorderColor: PropTypes.string,
  cardGlowColor: PropTypes.string,
  poweredByColor: PropTypes.string,
  sectionId: PropTypes.string,
  minHeightMobile: PropTypes.number,
  minHeightTablet: PropTypes.number,
  minHeightSmallDesktop: PropTypes.number,
  minHeightDesktop: PropTypes.number,
  showPoweredBy: PropTypes.bool,
  poweredByText: PropTypes.string,
  poweredByUrl: PropTypes.string,
  allowedOrigins: PropTypes.arrayOf(PropTypes.string),
};

const styles = {
  section: {
    width: "100%",
    padding: "40px 0",
    background: "transparent",
  },

  inner: {
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
  },

  header: {
    textAlign: "center",
    marginBottom: "20px",
  },

  title: {
    margin: "0 0 12px 0",
    fontFamily: "'Lobster', cursive",
    fontSize: "clamp(28px, 5vw, 48px)",
    lineHeight: 1.1,
    fontWeight: 400,
    textShadow: "0 0 18px rgba(127, 249, 242, 0.25)",
  },

  description: {
    margin: "0 auto",
    maxWidth: "820px",
    fontSize: "clamp(15px, 2vw, 18px)",
    lineHeight: 1.6,
  },

  embedCard: {
    width: "100%",
    borderRadius: "18px",
    overflow: "hidden",
  },

  iframe: {
    display: "block",
    width: "100%",
    border: "0",
    background: "transparent",
  },
    poweredBy: {
    margin: "12px 0 0 0",
    textAlign: "center",
    fontSize: "14px",
    lineHeight: 1.5,
  },

  poweredByLink: {
    textDecoration: "none",
    fontWeight: 700,
  },
};